import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Pencil, Save, X as XIcon, Trash2 } from "lucide-react";

export default function OverheadLedgersPage() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", active: true });
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", active: true });

    const load = async () => {
        const { data } = await api.get("/overhead-ledgers");
        setRows(data);
    };
    useEffect(() => {
        load();
    }, []);

    const submit = async () => {
        if (!form.name.trim()) return toast.error("Name required");
        try {
            await api.post("/overhead-ledgers", { name: form.name.trim(), active: form.active });
            toast.success("Ledger created");
            setOpen(false);
            setForm({ name: "", active: true });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const startEdit = (r) => {
        setEditingId(r.id);
        setEditForm({ name: r.name, active: r.active });
    };
    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({ name: "", active: true });
    };
    const saveEdit = async (id) => {
        try {
            await api.put(`/overhead-ledgers/${id}`, editForm);
            toast.success("Updated");
            cancelEdit();
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this ledger?")) return;
        try {
            await api.delete(`/overhead-ledgers/${id}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cannot delete");
        }
    };

    return (
        <div data-testid="overhead-ledgers-page">
            <PageHeader
                title="Overhead Ledgers"
                eyebrow="Masters"
                description="Expense classifications used when entering payments that aren't to a partner. Inactive ledgers won't appear in the Payments form."
                action={
                    isAdmin ? (
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="ol-add-btn">
                                    + New ledger
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm">
                                <DialogHeader>
                                    <DialogTitle>Create overhead ledger</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-3">
                                    <Field label="Name">
                                        <Input
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            className="rounded-sm h-9"
                                            data-testid="ol-name-input"
                                        />
                                    </Field>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[12px] text-slate-600">Active</span>
                                        <Switch
                                            checked={form.active}
                                            onCheckedChange={(v) => setForm({ ...form, active: v })}
                                            data-testid="ol-active-switch"
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" onClick={() => setOpen(false)}>
                                        Cancel
                                    </Button>
                                    <Button onClick={submit} className="rounded-sm bg-[#002FA7]" data-testid="ol-submit-btn">
                                        Create
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    ) : null
                }
            />

            <div className="bg-white border border-slate-200 overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-4 py-2.5">Ledger Name</th>
                            <th className="px-4 py-2.5 w-32">Status</th>
                            <th className="px-4 py-2.5 w-32 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={3} className="text-center text-slate-500 py-10">
                                    No overhead ledgers yet
                                </td>
                            </tr>
                        )}
                        {rows.map((r) =>
                            editingId === r.id ? (
                                <tr key={r.id} className="border-b border-slate-100 bg-blue-50/30">
                                    <td className="px-4 py-2">
                                        <Input
                                            value={editForm.name}
                                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                            className="h-8 rounded-sm"
                                            data-testid={`ol-edit-name-${r.id}`}
                                        />
                                    </td>
                                    <td className="px-4 py-2">
                                        <Switch
                                            checked={editForm.active}
                                            onCheckedChange={(v) => setEditForm({ ...editForm, active: v })}
                                            data-testid={`ol-edit-active-${r.id}`}
                                        />
                                    </td>
                                    <td className="px-4 py-2 text-right whitespace-nowrap">
                                        <Button
                                            size="sm"
                                            onClick={() => saveEdit(r.id)}
                                            className="h-7 px-2 rounded-sm bg-[#002FA7]"
                                            data-testid={`ol-edit-save-${r.id}`}
                                        >
                                            <Save size={12} />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-7 px-2 ml-1">
                                            <XIcon size={12} />
                                        </Button>
                                    </td>
                                </tr>
                            ) : (
                                <tr key={r.id} className="border-b border-slate-100" data-testid={`ol-row-${r.id}`}>
                                    <td className="px-4 py-2 font-medium">{r.name}</td>
                                    <td className="px-4 py-2">
                                        {r.active ? (
                                            <span className="text-[11px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                                                Active
                                            </span>
                                        ) : (
                                            <span className="text-[11px] uppercase tracking-widest text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5">
                                                Inactive
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-right whitespace-nowrap">
                                        {isAdmin && (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => startEdit(r)}
                                                    className="h-7 px-2"
                                                    data-testid={`ol-edit-${r.id}`}
                                                >
                                                    <Pencil size={13} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => remove(r.id)}
                                                    className="h-7 px-2 text-red-600"
                                                    data-testid={`ol-delete-${r.id}`}
                                                >
                                                    <Trash2 size={13} />
                                                </Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ),
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
